/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';

import { getWarehouseStockAvailQtyBulkAsync } from './DataService/DataActionExtension.g';
import { IGetWarehouseStockAvailQtyBulkResponse } from './DataService/DataServiceEntities.g';

const MAX_ITEM_IDS = 800;

/**
 * GetWarehouseStockAvailQtyBulk Input Action
 */
export class GetWarehouseStockAvailQtyBulkInput implements IActionInput {
    public itemIds: string[];
    public userName: string;
    public bypassCache: boolean;

    constructor(itemIds: string[], userName?: string, bypassCache?: boolean) {
        this.itemIds = itemIds;
        this.userName = userName || '';
        this.bypassCache = bypassCache || false;
    }

    public getCacheKey = () => `GetWarehouseStockAvailQtyBulk-${[...this.itemIds].sort().join(',')}`;
    public getCacheObjectType = () => 'GetWarehouseStockAvailQtyBulkResponse';
    public dataCacheType = (): CacheType => 'request';
}

const createInput = (args: ICreateActionContext, itemIds: string[], userName?: string): IActionInput => {
    const siteBypass = Boolean((args.requestContext as any)?.app?.config?.coverageAvailabilityBypassCache);
    return new GetWarehouseStockAvailQtyBulkInput(itemIds, userName, siteBypass);
};

export async function GetWarehouseStockAvailQtyBulk(
    input: GetWarehouseStockAvailQtyBulkInput,
    ctx: IActionContext
): Promise<IGetWarehouseStockAvailQtyBulkResponse> {
    if (!input.itemIds || input.itemIds.length === 0) {
        return { Success: true, ErrorMessage: '', Availabilities: [] };
    }

    if (input.itemIds.length > MAX_ITEM_IDS) {
        throw new Error(
            `[get-warehouse-stock-avail-qty-bulk] itemIds exceeds maximum of ${MAX_ITEM_IDS}. Received: ${input.itemIds.length}`
        );
    }

    const siteBypass = Boolean((ctx.requestContext as any)?.app?.config?.coverageAvailabilityBypassCache);
    const resolvedBypass = input.bypassCache || siteBypass;

    return getWarehouseStockAvailQtyBulkAsync({ callerContext: ctx }, input.itemIds, input.userName, resolvedBypass)
        .then(data => {
            return data;
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.trace('[get-warehouse-stock-avail-qty-bulk] Unable to load warehouse stock availability');
            throw new Error('[get-warehouse-stock-avail-qty-bulk] Unable to load warehouse stock availability');
        });
}

export default createObservableDataAction({
    action: <IAction<IGetWarehouseStockAvailQtyBulkResponse>>GetWarehouseStockAvailQtyBulk,
    id: 'GetWarehouseStockAvailQtyBulk',
    input: createInput
});
